const globalConfig = require('../../config');
const { get: _get } = require('lodash');
const { isProxy } = require('./mode');
// const { getXPathForElement } = require('../../proxy/services/sentence_merging.service');
// const { getXPathForElement } = require('../services/sentence-merging-service')

// ---------------------------------------------------------------------------
// Pre-compiled regex constants (created once at module load).
// Building RegExp objects on every function call was showing up as a hot spot
// during traversal — string inputs are re-processed many times per page.
// ---------------------------------------------------------------------------
const NBSP_CHAR_REGEX = new RegExp(String.fromCharCode(160), 'g');
// Preserving pre-existing (intentional) literal-slash patterns as-is.
const CLEAN_STRING_REGEXES = [
  new RegExp('/<!--.*-->/s', 'g'),
  new RegExp('~<!--(.*?)-->~s', 'g'),
  new RegExp('~<!--(.*?)-->~s', 'g'),
  new RegExp('/<!--(.|s)*?-->/', 'g'),
  new RegExp('/<!--(.|s)*?-->/', 'g')
];
const NON_ALNUM_ONLY_REGEX = new RegExp('/^[^a-zA-Z0-9]+$/', 'g');
const RUPEE_TRAILING_DIGITS_REGEX = /₹\s*\d+$/;
const DIGITS_ONLY_REGEX = new RegExp('/^d+$/', 'g');
const NON_ALNUM_DIGITS_REGEX = new RegExp('/^[^a-zA-Z0-9]+d+$/', 'g');

const REMOVE_EXTRA_SPACES_WHITESPACE_REGEX = /(\r\n|\n|\r|\t)/gm;
const REMOVE_EXTRA_SPACES_UNPRINTABLE_REGEX = /[^\x20-\x7E™®©₹✓\xB0\xD7\xF7]/gim;
const MULTIPLE_SPACES_REGEX = / +/g;

const JAPANESE_COMMENT_REGEX = /<!--.*?-->/gs;
const JAPANESE_WHITESPACE_REGEX = /[\r\n\t]/g;
const JAPANESE_NON_SCRIPT_REGEX = new RegExp(
  '/^[^p{Script=Han}p{Script=Hiragana}p{Script=Katakana}dー々〆〤ぁ-んァ-ンａ-ｚＡ-Ｚ0-9]+$/',
  'g'
);
const JAPANESE_DIGITS_ONLY_REGEX = new RegExp('/^d+$/', 'g');
const JAPANESE_DATE_PATTERN_REGEX = new RegExp('\\d+-\\d+-\\d+', 'g');
const JAPANESE_NON_SCRIPT_TRAILING_DIGITS_REGEX = new RegExp(
  '/^[^p{Script=Han}p{Script=Hiragana}p{Script=Katakana}d]+d+$/',
  'g'
);

// ---------------------------------------------------------------------------
// Bounded FIFO cache for cleanString / cleanStringJapanese.
// The same short strings (labels, headings, single words) are cleaned dozens
// of times per page — memoizing eliminates redundant regex work.
// Cap prevents unbounded memory growth on pages with lots of unique text.
// ---------------------------------------------------------------------------
const CLEAN_STRING_CACHE_MAX = 2000;
const cleanStringCache = new Map();
const cleanStringJapaneseCache = new Map();

function _cacheGet(cache, key) {
  return cache.get(key);
}

function _cacheSet(cache, key, value) {
  if (cache.size >= CLEAN_STRING_CACHE_MAX) {
    // FIFO eviction: remove oldest (Map iterates in insertion order).
    const oldestKey = cache.keys().next().value;
    cache.delete(oldestKey);
  }
  cache.set(key, value);
}

function removeExtraSpacesInMiddle(str) {
  if (str) {
    const str1 = str.replace(REMOVE_EXTRA_SPACES_WHITESPACE_REGEX, ' ');
    let str2 = str1.replace(REMOVE_EXTRA_SPACES_UNPRINTABLE_REGEX, '');

    return str2.replace(MULTIPLE_SPACES_REGEX, ' ');
  }
  return str;
}

function cleanString(str = '', trim = true) {
  // Cache key includes trim flag so trim=true and trim=false don't collide.
  const cacheKey = (trim ? 't|' : 'f|') + str;
  const cached = _cacheGet(cleanStringCache, cacheKey);
  if (cached !== undefined) return cached;

  let outputString = str;
  outputString = outputString.replaceAll(NBSP_CHAR_REGEX, ' ');
  outputString = outputString.replaceAll('&nbsp;', ' ');
  outputString = outputString.replaceAll('&gt;', '>');
  outputString = outputString.replaceAll('&lt;', '<');
  outputString = outputString.replaceAll('&amp;', '&');
  for (let i = 0; i < CLEAN_STRING_REGEXES.length; i++) {
    outputString = outputString.replace(CLEAN_STRING_REGEXES[i], '');
  }
  outputString = outputString.replace('\n', '');
  outputString = outputString.replace('\r', '');
  outputString = outputString.replace('\t', '');
  outputString = outputString.replace('\xc2\xa0', '');
  outputString = outputString.replace(NON_ALNUM_ONLY_REGEX, '');
  // remove ₹ followed by numbers only
  outputString = outputString.replace(RUPEE_TRAILING_DIGITS_REGEX, '');
  outputString = outputString.replace(DIGITS_ONLY_REGEX, '');
  outputString = outputString.replace(NON_ALNUM_DIGITS_REGEX, '');
  outputString = outputString.length == 1 ? '' : outputString;
  outputString = removeExtraSpacesInMiddle(outputString);
  if (trim) {
    outputString = outputString.trim();
  }
  outputString = outputString.length > 0 ? outputString : '';

  const result = isNaN(outputString) ? outputString : '';
  _cacheSet(cleanStringCache, cacheKey, result);
  return result;
}

function cleanStringJapanese(str = '', trim = true) {
  const cacheKey = (trim ? 't|' : 'f|') + str;
  const cached = _cacheGet(cleanStringJapaneseCache, cacheKey);
  if (cached !== undefined) return cached;

  let outputString = str;
  outputString = outputString.replace(NBSP_CHAR_REGEX, '');
  outputString = outputString.replace(JAPANESE_COMMENT_REGEX, '');
  outputString = outputString.replace(JAPANESE_WHITESPACE_REGEX, '');
  outputString = outputString.replace('\xc2\xa0', '');
  outputString = outputString.replace(JAPANESE_NON_SCRIPT_REGEX, '');
  outputString = outputString.replace(JAPANESE_DIGITS_ONLY_REGEX, '');
  outputString = outputString.replace(JAPANESE_DATE_PATTERN_REGEX, '');
  outputString = outputString.replace(JAPANESE_NON_SCRIPT_TRAILING_DIGITS_REGEX, '');
  outputString = outputString.replace(REMOVE_EXTRA_SPACES_WHITESPACE_REGEX, ' ');
  outputString = outputString.replace(MULTIPLE_SPACES_REGEX, ' ');

  if (trim) {
    outputString = outputString.trim();
  }

  outputString = outputString.length > 0 ? outputString : '';

  const result = isNaN(outputString) ? outputString : '';
  _cacheSet(cleanStringJapaneseCache, cacheKey, result);
  return result;
}

// ---------------------------------------------------------------------------
// yieldToMain: give the browser a chance to render / handle input between
// long-running work. Use the modern Scheduler API when available, fall back
// to postTask, then MessageChannel (0ms), then setTimeout(0).
// MessageChannel is faster than setTimeout(0) which is clamped to ~4ms.
// ---------------------------------------------------------------------------
let _yieldChannel = null;
function _getYieldChannel() {
  if (!_yieldChannel && typeof MessageChannel !== 'undefined') {
    _yieldChannel = new MessageChannel();
  }
  return _yieldChannel;
}

function yieldToMain() {
  const g = typeof globalThis !== 'undefined' ? globalThis : self;
  const sched = g && g.scheduler;
  // scheduler.yield() — best option, prioritizes continuation
  if (sched && typeof sched.yield === 'function') {
    return sched.yield();
  }
  // scheduler.postTask() — second best
  if (sched && typeof sched.postTask === 'function') {
    return sched.postTask(() => {}, { priority: 'user-visible' });
  }
  // MessageChannel — faster than setTimeout(0) (no 4ms clamp)
  const channel = _getYieldChannel();
  if (channel) {
    return new Promise((resolve) => {
      channel.port1.onmessage = () => resolve();
      channel.port2.postMessage(null);
    });
  }
  // Last resort
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Get configuration value from the global config
 * based on dot notation syntax.
 *
 * @param {string} path Path to the object property
 * @returns mixed
 */
function getConfig(path) {
  return _get(globalConfig, path);
}

/**
 * Get configuration of a specific feature.
 *
 * @param {string} name Name of the feature
 * @returns object
 */
function getFeatureConfig(name) {
  return getConfig(`features.${name}`);
}

/**
 * Get configuration of a specific client.
 *
 * @param {string} name Name of the client
 * @returns object
 */
function getClientConfig(name) {
  return getConfig(`clients.${name}`);
}

function removeInternalLinkFromUrl(url = '', keepQueryParams = false) {
  return url;
  const hashIndex = url.indexOf('#');
  const questionIndex = url.indexOf('?');
  const internalLinkExist = hashIndex > 0;
  const queryExist = questionIndex > 0;
  const queryIsAfterInternalLink = questionIndex > hashIndex;
  if (internalLinkExist) {
    if (keepQueryParams && queryExist && queryIsAfterInternalLink) {
      return url.slice(0, hashIndex) + url.slice(questionIndex, url.length);
    }

    return url.slice(0, hashIndex);
  }
  return url;
}

function formatTranslationDataByLocalKey(translationDataByOrigKey) {
  const translationDataObjByLocalKey = {};
  for (const [key, value] of Object.entries(translationDataByOrigKey)) {
    translationDataObjByLocalKey[value.toLowerCase()] = key;
  }
  return translationDataObjByLocalKey;
}

function setTranslatedTextToSentenceObj(sentenceObj) {
  if (
    sourceLanguageCleanString(sentenceObj.original_text) ===
    sourceLanguageCleanString(sentenceObj.translated_text)
  ) {
    return;
  }
  let translatedTextsArr = [];
  if (sentenceObj.translated_text) {
    translatedTextsArr = sentenceObj.translated_text.split(
      globalConfig.sentence_merge_separator_receive
    );
  }

  const children = sentenceObj.children.map((child, index) => {
    child.translated_text = translatedTextsArr[index] ? translatedTextsArr[index] : '';
    return child;
  });
  sentenceObj.children = children;
  return sentenceObj;
}

function setTranslatedTextToSentenceMapChildren(sentenceMap) {
  const obj = {};
  Object.entries(sentenceMap).forEach(([key, value]) => {
    if (
      sourceLanguageCleanString(value.original_text) !==
      sourceLanguageCleanString(value.translated_text)
    ) {
      let translatedTextsArr = [];
      if (value.translated_text) {
        translatedTextsArr = value.translated_text.split(
          globalConfig.sentence_merge_separator_receive
        );
      }

      const children = value.children.map((child, index) => {
        child.translated_text = translatedTextsArr[index];
        return child;
      });
      value.children = children;
    }
    obj[key] = value;
  });
  return obj;
}

// showing unauthorized message
function uiErrorContextEditor() {
  const element = document.getElementById('devnagri_uiErrorContextEditor');
  if (element) {
    element.remove();
  }
  const uiErrorEditor = document.createElement('div');
  uiErrorEditor.innerText = 'You are unauthorized please use new token';
  uiErrorEditor.classList.add('devnagri_uiErrorEditor');
  uiErrorEditor.id = 'devnagri_uiErrorContextEditor';
  const outerMostDiv = document.getElementById('devnagri_outermostdiv');
  outerMostDiv.appendChild(uiErrorEditor);
}

// this will create ui for unauthorized message
function showErrorMessage() {
  uiErrorContextEditor();
}

function compareArrays(a, b) {
  return a.length === b.length && a.every((element, index) => element === b[index]);
}

// this function is used for reloading when text is edited but don't want to send text to api
function webOwnerCancel() {
  location.reload();
}

// handling click event for editing
function handler(e) {
  if (
    ![
      'devnagri_owner_button Devnagri-tooltip-toggle',
      'devnagri_owner_send_button',
      'devnagri_owner_cancel_button',
      'devnagri_open_button',
      'devnagri_container',
      'devnagri_optionElement',
      'devnagri_seo_cancel',
      'devnagri_seo_submit',
      'devnagri_seo',
      'devnagri_logo',
      'devnagri_written_lang',
      'devnagri_upArrow',
      'devnagri_outermostdivclass'
    ].includes(e.target.className)
  ) {
    e.stopPropagation();
    e.preventDefault();
    e.stopImmediatePropagation();
  }
}

// async function getXPathForElement(el, xml, att = null) {
//   let xpath = '';
//   let pos;
//   let tempitem2;

//   while (el !== xml.documentElement) {
//     let nodeName = await el.nodeName
//     pos = 0;
//     tempitem2 = el;
//     while (tempitem2) {
//       if (tempitem2.nodeType === 1 && tempitem2.nodeName === nodeName) {
//         // If it is ELEMENT_NODE of the same name
//         pos += 1;
//       }
//       tempitem2 = tempitem2.previousSibling;
//     }

//     xpath = `*[name()='${nodeName}' and namespace-uri()='${
//       el.namespaceURI ?? ''
//     }'][${pos}]/${xpath}`;

//     el = el.parentNode;
//   }
//   xpath = `/*[name()='${xml.documentElement.nodeName}' and namespace-uri()='${
//     el.namespaceURI ?? ''
//   }']/${xpath}`;
//   if (att) {
//     xpath = `${xpath}/*[attr()='${att}' and namespace-uri()='${el.namespaceURI ?? ''}']`;
//   }
//   xpath = xpath.replace(/\/$/, '');
//   return xpath;
// }

function getXPathForElement(el, xml, att = null) {
  let xpath = '';
  let pos;
  let tempitem2;

  while (el && el !== xml.documentElement) {
    pos = 0;
    tempitem2 = el;
    while (tempitem2) {
      if (
        (tempitem2.nodeType === 1 || tempitem2.nodeType === 3) &&
        tempitem2.nodeName === el.nodeName
      ) {
        // If it is ELEMENT_NODE of the same name
        pos += 1;
      }
      tempitem2 = tempitem2.previousSibling;
    }

    xpath = el.nodeName
      ? `${el.nodeName.toLowerCase()}${pos > 1 ? `[${pos}]` : ''}/${xpath}`
      : xpath;

    el = el.parentNode;
  }
  xpath = `/${xml.documentElement.nodeName.toLowerCase()}/${xpath}`;

  xpath = xpath.replace(/\/$/, '');

  if (att) {
    xpath = `${xpath}#${att}`;
  }

  return xpath;
}

// This function helps us find the XPath of each element and its parent element.
function pushXpathToSentencesArray(node, text, document, att) {
  var xpath = getXPathForElement(node, document, att);
  var xpathParent = getXPathForElement(node.parentElement, document);
  var temp = { sentence: text, xpath: xpath, xpathParent: xpathParent };
  globalConfig.sentence['sentences'].push(temp);
  return { xpath, xpathParent };
}

function mergedSentencesXpath(sentenceNode, document) {
  const mergedSentences = Object.entries(sentenceNode).map(
    ([xpathParent, { children, original_text, _idx }]) => {
      let originalText = original_text;
      let childXpath =
        children &&
        children.map((value) => {
          const currentChildXpath = getXPathForElement(value.node, document);
          const isExistingChildXpath = globalConfig.existingChildXpaths.includes(currentChildXpath);
          if (isExistingChildXpath) {
            originalText = undefined;
          }
          return currentChildXpath;
        });
      return {
        sentence: originalText,
        xpath: xpathParent,
        xpathParent,
        xpathChild: childXpath,
        _idx
      };
    }
  );
  return mergedSentences.filter((item) => item.sentence != undefined);
}

function isObjEmpty(obj) {
  return _.isEmpty(obj);
}

function isIndexDbSupportedByBrowser() {
  return window.indexedDB ? true : false;
}

/**
 * check if the segment contains only 'url', 'email', 'numbers' or 'special characters'
 * @param {string} segment
 * @returns boolean
 */
function checkIfSegmentIsValid(segment) {
  if (globalConfig.source_lang_code === 'en') {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const urlPattern = /^https?:\/\/[^\s]+$/;
    const specialCharactersPattern = /^\s*[^a-zA-Z]*\s*$/;

    // check for email or URL
    if (emailPattern.test(segment) || urlPattern.test(segment)) {
      return false;
    }

    // check for segments that are only numbers/special characters
    if (specialCharactersPattern.test(segment)) {
      return false;
    }

    return true;
  }
  return true;
}

function languageBasedCleanString(str = '', trim = true, languageCode) {
  let outputString = str;
  const removeNbspRegEx = new RegExp(String.fromCharCode(160), 'g');
  outputString = outputString.replace(removeNbspRegEx, ' ');
  outputString = outputString.replace(/<!--.*?-->/gs, '');
  outputString = outputString.replace('\n', '');
  outputString = outputString.replace('\r', '');
  outputString = outputString.replace('\t', '');
  outputString = outputString.replace('\xc2\xa0', '');
  const languagePatterns = {
    pa: [new RegExp(/^[^0-9\u0A00-\u0A7F@-\s]+$/, 'g')],
    hi: [new RegExp(/^[^0-9\u0900-\u097F@-\s]+$/, 'g')]
  };
  if (languagePatterns[languageCode]) {
    languagePatterns[languageCode].forEach((pattern) => {
      outputString = outputString.replace(pattern, '');
    });
  }

  const emojiPre = /[\p{Emoji_Presentation}]+/gu;
  // const emojiReg = /[\p{Emoji}]+/gu;
  classicalBuildingEmoji = /\u{1F3DB}/gu;

  outputString = emojiPre.test(outputString) ? outputString.replace(emojiPre, '') : outputString;

  outputString = classicalBuildingEmoji.test(outputString)
    ? outputString.replace(classicalBuildingEmoji, '')
    : outputString;

  outputString = outputString.replace(new RegExp('/^d+$/', 'g'), '');
  outputString = outputString.replace(/(\r\n|\n|\r|\t)/gm, ' ');
  outputString = outputString.replace(/ +/g, ' ');
  outputString = outputString.replace(/^\|$/, '');

  if (trim) {
    outputString = outputString.trim();
  }

  outputString = outputString.length > 0 ? outputString : '';

  if (isNaN(outputString)) {
    return outputString;
  }

  return '';
}

/**
 *
 * @param {string} string
 * @param {boolean} isTrim
 * @returns source language specific cleanString function
 */
function sourceLanguageCleanString(string, isTrim = true) {
  switch (globalConfig.source_lang_code) {
    case 'en': {
      return cleanString(string, isTrim);
    }
    case 'ja': {
      return cleanStringJapanese(string, isTrim);
    }
    case 'hi': {
      return languageBasedCleanString(string, isTrim, globalConfig.source_lang_code);
    }
    case 'pa': {
      return languageBasedCleanString(string, isTrim, globalConfig.source_lang_code);
    }
    default: {
      return string;
    }
  }
}

function formatUrl(
  url,
  domain = globalConfig.domain,
  requestedDomain = globalConfig.requested_domain,
  isProxyMode = false,
  domainSDKEnabled = globalConfig.domainSDKEnabled
) {
  // Perform domain Proxy specific URL formatting
  if (isProxyMode) {
    url = url.replaceAll(requestedDomain, domain);
  }

  // Perform domain SDK specific URL formatting
  if (domainSDKEnabled && domain) {
    if (domain.endsWith('/')) {
      domain = domain.slice(0, -1);
    }
    const urlObj = new URL(url);
    const lastSegment = urlObj.pathname.split('/').filter(Boolean).pop();
    return `${domain}/${lastSegment}`;
  }
  return url;
}

module.exports = {
  removeExtraSpacesInMiddle,
  cleanString,
  yieldToMain,
  getConfig,
  getFeatureConfig,
  getClientConfig,
  removeInternalLinkFromUrl,
  formatTranslationDataByLocalKey,
  setTranslatedTextToSentenceObj,
  setTranslatedTextToSentenceMapChildren,
  uiErrorContextEditor,
  showErrorMessage,
  compareArrays,
  webOwnerCancel,
  handler,
  pushXpathToSentencesArray,
  getXPathForElement,
  mergedSentencesXpath,
  isObjEmpty,
  checkIfSegmentIsValid,
  isIndexDbSupportedByBrowser,
  sourceLanguageCleanString,
  formatUrl
};
